import { useQuery } from '@tanstack/react-query';
import type { CountryProfile } from '@arriba.digital/contracts';
import { settingsApi } from '../api/resources';
import { queryKeys } from '../api/query-keys';

/**
 * The workspace's country, as the interface needs it.
 *
 * Every tax-shaped control in the product reads its options from here instead
 * of from a list compiled into the bundle: the IVA rates on a product, the
 * regimes and identifier types on a counterparty, the provinces, the document
 * classes, the fields of the fiscal settings form. Those lists were constants
 * once, and they were Argentina's — a workspace in Chile was offered a CUIT,
 * four IVA rates it does not have, and twenty-four provinces it is not divided
 * into.
 *
 * Cached for the life of the session: a country is decided at sign-up and does
 * not change while somebody is signed in, so refetching it would only add a
 * request per screen. A reload re-asks, which is also when a pack change
 * should take effect.
 *
 * `profile` is `undefined` until the first fetch resolves. Callers that render
 * a picker should hold off rather than fall back to a guess — a wrong tax rate
 * is worse than a spinner.
 */
export function useCountryProfile() {
  const query = useQuery({
    queryKey: queryKeys.country,
    queryFn: () => settingsApi.country(),
    staleTime: Infinity,
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * The country profile, for a screen that cannot render without it.
 *
 * Returns the same object as {@link useCountryProfile} but narrows `profile`
 * to a non-nullable value once loaded, so callers do not repeat the guard.
 */
export function useRequiredCountryProfile(): {
  profile: CountryProfile | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { profile, isLoading, isError } = useCountryProfile();
  return { profile: profile ?? null, isLoading, isError };
}
